/**
 * NotificationPrompt - Component to request push notification permission
 * 
 * Shows a friendly prompt to enable notifications for:
 * - Daily horoscope updates
 * - Oracle reminders
 * - Cosmic events
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Sparkles } from 'lucide-react';
import { usePushNotifications } from '@/hooks/useFirebaseServices';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAuthHeaders } from '@/utils/api';

const NotificationPrompt = ({ onClose, onEnabled }) => {
    const { permission, isSupported, requestPermission } = usePushNotifications();
    const { language } = useLanguage();
    const [isLoading, setIsLoading] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    
    const isSpanish = language === 'es';
    
    // Check if we should show the prompt
    useEffect(() => {
        if (!isSupported) return;
        if (permission === 'granted') return;
        
        // Check if user has dismissed before
        const dismissed = localStorage.getItem('notification_prompt_dismissed');
        if (dismissed) {
            const dismissedDate = new Date(dismissed);
            const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
            // Show again after 7 days
            if (daysSinceDismissed < 7) return;
        }
        
        // Show prompt after 3 seconds on dashboard
        const timer = setTimeout(() => setShowPrompt(true), 3000);
        return () => clearTimeout(timer);
    }, [isSupported, permission]);
    
    const handleEnable = async () => {
        setIsLoading(true);
        const token = await requestPermission();
        setIsLoading(false);
        
        if (token) {
            // Register token with backend (PHASE 2B)
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const headers = await getAuthHeaders();
                await fetch(`${backendUrl}/api/notifications/register-token`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ fcm_token: token, device_type: 'web' })
                });
                console.log('📬 FCM token registered with backend');
            } catch (err) {
                console.error('Failed to register FCM token:', err);
            }
            
            onEnabled?.();
            setShowPrompt(false);
        }
    };
    
    const handleDismiss = () => {
        localStorage.setItem('notification_prompt_dismissed', new Date().toISOString());
        setShowPrompt(false);
        onClose?.();
    };
    
    if (!showPrompt) return null;
    
    return (
        <AnimatePresence>
            <motion.div
                className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.9 }}
            >
                <div className="bg-gradient-to-br from-gray-900 to-black border border-primary/30 rounded-2xl p-4 shadow-xl">
                    {/* Close button */}
                    <button
                        onClick={handleDismiss}
                        className="absolute top-3 right-3 text-primary/40 hover:text-primary/60 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    
                    {/* Icon */}
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Bell className="w-6 h-6 text-primary" />
                        </div>
                        
                        <div className="flex-1 pr-4">
                            <h3 className="text-primary font-cinzel text-sm mb-1">
                                {isSpanish ? 'Mensajes Cósmicos' : 'Cosmic Messages'}
                            </h3>
                            <p className="text-primary/60 text-xs font-inter mb-3">
                                {isSpanish 
                                    ? 'Recibe tu horóscopo diario y alertas de eventos cósmicos'
                                    : 'Receive your daily horoscope and cosmic event alerts'}
                            </p>
                            
                            <div className="flex gap-2">
                                <button
                                    onClick={handleEnable}
                                    disabled={isLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/40 rounded-lg text-primary text-xs font-inter hover:bg-primary/30 transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Sparkles className="w-3 h-3" />
                                            {isSpanish ? 'Activar' : 'Enable'}
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleDismiss}
                                    className="px-4 py-2 text-primary/40 text-xs font-inter hover:text-primary/60 transition-colors"
                                >
                                    {isSpanish ? 'Ahora no' : 'Not now'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default NotificationPrompt;
